import React, { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, X, Globe } from "lucide-react";
import { useLang } from "@/context/LangContext";
import { useSettings } from "@/context/SettingsContext";
import { waLink } from "@/lib/wa";

const NAV = [
  { to: "/", key: "nav.home" },
  { to: "/our-coffee", key: "nav.coffee" },
  { to: "/available-stock", key: "nav.stock" },
  { to: "/our-warehouse", key: "nav.warehouse" },
  { to: "/export-journey", key: "nav.journey" },
  { to: "/quality", key: "nav.quality" },
  { to: "/about", key: "nav.about" },
  { to: "/contact", key: "nav.contact" },
];

export const Header = () => {
  const { t, lang, changeLang } = useLang();
  const { settings } = useSettings();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => { setOpen(false); }, [location.pathname]);

  return (
    <header
      data-testid="site-header"
      className={`fixed inset-x-0 top-0 z-50 transition-all duration-300 ${
        scrolled ? "bg-offwhite/90 shadow-sm backdrop-blur-md border-b border-forest/10" : "bg-gradient-to-b from-black/50 via-black/20 to-transparent"
      }`}
    >
      <div className="mx-auto flex max-w-[1400px] items-center justify-between gap-4 px-4 py-3 sm:px-8 lg:px-12">
        <Link to="/" data-testid="header-logo-link" className="flex items-center gap-3">
          <img src={settings.logo || "/logo.jpg"} alt="Warehouse Coffee logo" className="h-11 w-11 rounded object-contain bg-white p-0.5" />
          <div className="leading-none">
            <div className={`font-display text-base font-extrabold tracking-tight ${scrolled ? "text-forest-dark" : "text-offwhite"}`}>WAREHOUSE COFFEE</div>
            <div className={`mt-0.5 font-mono text-[9px] uppercase tracking-[0.25em] ${scrolled ? "text-espresso/60" : "text-offwhite/60"}`}>Indonesian Arabica · Estd 2020</div>
          </div>
        </Link>

        <nav className="hidden items-center gap-6 xl:flex">
          {NAV.map((n) => (
            <Link
              key={n.to}
              to={n.to}
              data-testid={`nav-${n.key.split(".")[1]}-link`}
              className={`font-body text-sm font-medium transition-colors hover:text-amber-brand ${
                location.pathname === n.to ? (scrolled ? "text-forest" : "text-amber-soft") : scrolled ? "text-espresso/80" : "text-offwhite drop-shadow-[0_1px_3px_rgba(0,0,0,0.5)]"
              }`}
            >
              {t(n.key)}
            </Link>
          ))}
        </nav>

        <div className="flex items-center gap-2">
          <button
            data-testid="language-switcher-id"
            onClick={() => changeLang(lang === "en" ? "id" : "en")}
            className={`hidden items-center gap-1.5 rounded-full border px-3 py-1.5 font-mono text-xs font-semibold uppercase transition-colors hover:bg-forest hover:text-offwhite sm:flex ${scrolled ? "border-forest/20 text-forest" : "border-offwhite/40 text-offwhite"}`}
          >
            <Globe className="h-3.5 w-3.5" />
            {lang === "en" ? "EN" : "ID"}
          </button>
          <Link
            to="/request-quote"
            data-testid="header-quote-cta"
            className="hidden rounded-full bg-amber-brand px-5 py-2.5 font-display text-xs font-bold uppercase tracking-wider text-espresso-dark transition-all hover:bg-amber-soft md:inline-block"
          >
            {t("cta.quote")}
          </Link>
          <button
            data-testid="mobile-menu-toggle"
            onClick={() => setOpen((v) => !v)}
            className={`flex h-10 w-10 items-center justify-center rounded-md xl:hidden ${scrolled || open ? "text-forest" : "text-offwhite"}`}
          >
            {open ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
          </button>
        </div>
      </div>

      {open && (
        <div data-testid="mobile-menu" className="border-t border-forest/10 bg-offwhite/98 backdrop-blur-md xl:hidden">
          <nav className="flex flex-col px-4 py-3 sm:px-8">
            {NAV.map((n) => (
              <Link
                key={n.to}
                to={n.to}
                data-testid={`mobile-nav-${n.key.split(".")[1]}-link`}
                className="border-b border-forest/5 py-3 font-body text-sm font-medium text-espresso/85"
              >
                {t(n.key)}
              </Link>
            ))}
            <div className="mt-3 flex gap-2">
              <Link to="/request-quote" className="flex-1 rounded-full bg-forest py-3 text-center font-display text-xs font-bold uppercase tracking-wider text-offwhite">
                {t("cta.quote")}
              </Link>
              <a href={waLink(settings.whatsapp)} target="_blank" rel="noreferrer" className="flex-1 rounded-full border border-forest py-3 text-center font-display text-xs font-bold uppercase tracking-wider text-forest">
                {t("cta.whatsapp")}
              </a>
            </div>
            <button
              onClick={() => changeLang(lang === "en" ? "id" : "en")}
              className="mt-3 self-start rounded-full border border-forest/20 px-3 py-1.5 font-mono text-xs font-semibold uppercase text-forest"
            >
              <Globe className="mr-1 inline h-3.5 w-3.5" />{lang === "en" ? "English" : "Bahasa Indonesia"}
            </button>
          </nav>
        </div>
      )}
    </header>
  );
};

export default Header;
